// Chapter registry - single source of truth for all 31 chapters.
//
// EQUAL-DEPTH ARCHITECTURE
// ------------------------
// Every chapter is a ChapterModule (see ./types.ts) with the same 13 sections:
//   overview, objectives, coreConcepts, glossary, procedures, examples,
//   comparisons, misconceptions, examChecklist, flashcards, quiz,
//   reviewPrompts, recap.
//
// Two sources of content:
//   1. Hand-curated modules (e.g. chapter01_module.ts) - richest depth.
//   2. Auto-generated modules from _generated_modules.ts, produced by
//      /home/user/workspace/build_modules.py from each chapter's PDF text.
//
// A curated module always overrides the generated one when both exist.
// The UI never branches on depth - empty sections simply hide.

import type { ChapterModule } from "./types";
import chapter01Module from "./chapter01_module";
import chapter02Module from "./chapter02_module";
import chapter03Module from "./chapter03_module";
import chapter04Module from "./chapter04_module";
import chapter05Module from "./chapter05_module";
import chapter06Module from "./chapter06_module";
import chapter07Module from "./chapter07_module";
import chapter08Module from "./chapter08_module";
import chapter09Module from "./chapter09_module";
import chapter10Module from "./chapter10_module";
import chapter11Module from "./chapter11_module";
import chapter12Module from "./chapter12_module";
import chapter13Module from "./chapter13_module";
import chapter14Module from "./chapter14_module";
import chapter15Module from "./chapter15_module";
import chapter16Module from "./chapter16_module";
import chapter17Module from "./chapter17_module";
import chapter18Module from "./chapter18_module";
import chapter19Module from "./chapter19_module";
import chapter20Module from "./chapter20_module";
import chapter21Module from "./chapter21_module";
import chapter22Module from "./chapter22_module";
import chapter23Module from "./chapter23_module";
import chapter24Module from "./chapter24_module";
import chapter25Module from "./chapter25_module";
import chapter26Module from "./chapter26_module";
import chapter27Module from "./chapter27_module";
import chapter28Module from "./chapter28_module";
import chapter29Module from "./chapter29_module";
import chapter30Module from "./chapter30_module";
import chapter31Module from "./chapter31_module";
import { GENERATED_MODULES } from "./_generated_modules";
import { CURATION_STATUS } from "./curation-status";

// --- Catalog metadata (canonical titles + part groupings) ----------------
const CATALOG: { number: number; title: string; subtitle: string; part: string }[] = [
  { number: 1, title: "Definition and Characteristics of Applied Behavior Analysis", subtitle: "Goals of science, attitudes, history, dimensions, definition, domains", part: "Part 1 - Introduction and Basic Concepts" },
  { number: 2, title: "Basic Concepts and Principles", subtitle: "Behavior, response classes, respondent vs operant, 3-term contingency", part: "Part 1 - Introduction and Basic Concepts" },
  { number: 3, title: "Selecting and Defining Target Behaviors", subtitle: "Assessment methods, target selection, operational definitions", part: "Part 2 - Selecting, Defining, and Measuring Behavior" },
  { number: 4, title: "Measuring Behavior", subtitle: "Dimensional quantities, event/duration/latency measurement", part: "Part 2 - Selecting, Defining, and Measuring Behavior" },
  { number: 5, title: "Improving and Assessing the Quality of Behavioral Measurement", subtitle: "Validity, accuracy, reliability, IOA", part: "Part 2 - Selecting, Defining, and Measuring Behavior" },
  { number: 6, title: "Constructing and Interpreting Graphic Displays of Behavioral Data", subtitle: "Line graphs, axes, conditions, visual analysis", part: "Part 3 - Evaluating and Analyzing Behavior Change" },
  { number: 7, title: "Analyzing Behavior Change: Basic Assumptions and Strategies", subtitle: "Functional relations, IV/DV, single-subject logic", part: "Part 3 - Evaluating and Analyzing Behavior Change" },
  { number: 8, title: "Reversal and Multielement Designs", subtitle: "A-B-A-B, NCR, alternating treatments", part: "Part 3 - Evaluating and Analyzing Behavior Change" },
  { number: 9, title: "Multiple Baseline and Changing Criterion Designs", subtitle: "Across behaviors / subjects / settings; probe variants", part: "Part 3 - Evaluating and Analyzing Behavior Change" },
  { number: 10, title: "Planning and Evaluating Applied Behavior Analysis Research", subtitle: "Threats to validity, internal/external/social validity", part: "Part 3 - Evaluating and Analyzing Behavior Change" },
  { number: 11, title: "Positive Reinforcement", subtitle: "Unconditioned and conditioned reinforcers, schedules basics", part: "Part 4 - Reinforcement" },
  { number: 12, title: "Negative Reinforcement", subtitle: "Escape vs avoidance, SR-, free-operant avoidance", part: "Part 4 - Reinforcement" },
  { number: 13, title: "Schedules of Reinforcement", subtitle: "FR, VR, FI, VI; compound and concurrent schedules", part: "Part 4 - Reinforcement" },
  { number: 14, title: "Positive Punishment", subtitle: "SP+, response cost vs additive aversives, side effects", part: "Part 5 - Punishment" },
  { number: 15, title: "Negative Punishment", subtitle: "Time-out, response cost", part: "Part 5 - Punishment" },
  { number: 16, title: "Motivating Operations", subtitle: "EO/AO, value-altering & behavior-altering effects", part: "Part 6 - Antecedent Variables" },
  { number: 17, title: "Stimulus Control", subtitle: "SD/SΔ, discrimination, generalization gradients", part: "Part 6 - Antecedent Variables" },
  { number: 18, title: "Verbal Behavior", subtitle: "Mand, tact, echoic, intraverbal; Skinner's analysis", part: "Part 7 - Verbal Behavior" },
  { number: 19, title: "Equivalence-Based Instruction", subtitle: "Stimulus equivalence, reflexivity, symmetry, transitivity", part: "Part 8 - Developing New Behavior" },
  { number: 20, title: "Engineering Emergent Learning with Nonequivalence Relations", subtitle: "Relational frames, derived relational responding", part: "Part 8 - Developing New Behavior" },
  { number: 21, title: "Imitation, Modeling, and Observational Learning", subtitle: "Generalized imitation, video modeling", part: "Part 8 - Developing New Behavior" },
  { number: 22, title: "Shaping", subtitle: "Successive approximations, differential reinforcement", part: "Part 8 - Developing New Behavior" },
  { number: 23, title: "Chaining", subtitle: "Task analysis, forward/backward/total task chaining", part: "Part 8 - Developing New Behavior" },
  { number: 24, title: "Extinction", subtitle: "Extinction burst, spontaneous recovery, resistance to extinction", part: "Part 9 - Decreasing Behavior with Nonpunishment Procedures" },
  { number: 25, title: "Differential Reinforcement", subtitle: "DRA, DRI, DRO, DRL", part: "Part 9 - Decreasing Behavior with Nonpunishment Procedures" },
  { number: 26, title: "Antecedent Interventions", subtitle: "NCR, high-p sequences, functional communication training", part: "Part 9 - Decreasing Behavior with Nonpunishment Procedures" },
  { number: 27, title: "Functional Behavior Assessment", subtitle: "Indirect, descriptive, and experimental analysis", part: "Part 10 - Functional Assessment" },
  { number: 28, title: "Token Economy, Group Contingencies, and Contingency Contracting", subtitle: "Conditioned generalized reinforcers, independent / interdependent / dependent contingencies", part: "Part 11 - Special Applications" },
  { number: 29, title: "Self-Management", subtitle: "Self-monitoring, self-evaluation, self-instruction", part: "Part 11 - Special Applications" },
  { number: 30, title: "Generalization and Maintenance of Behavior Change", subtitle: "Stokes & Baer tactics, programming common stimuli", part: "Part 12 - Promoting Generalized Behavior Change" },
  { number: 31, title: "Ethical and Professional Responsibilities of Applied Behavior Analysts", subtitle: "BACB Ethics Code, informed consent, scope of competence", part: "Part 13 - Ethics" },
];

const pad = (n: number) => n.toString().padStart(2, "0");

/** PDF filename per chapter id. Files live in R2 bucket `aba-pdfs` and are served via the `aba-pdf-gateway` Worker at `/api/pdfs/<filename>` (Cloudflare Access + Referer-gated). */
const CHAPTER_PDFS: Record<string, string> = {
  "01": "Chapter-1-Definition-and-Characteristics-of-Applied-Behavior-Analysis.pdf",
  "02": "Chapter-2-Basic-Concepts-and-Principles.pdf",
  "03": "Chapter-3-Selecting-and-Defining-Target-Behaviors-2.pdf",
  "04": "Chapter-4-Measuring-Behavior-3.pdf",
  "05": "Chapter-5-Improving-and-Assessing-the-Quality-of-Behavioral-Measurement-4.pdf",
  "06": "Chapter-6-Constructing-and-Interpreting-Graphic-Displays-of-Behavioral-Data-5.pdf",
  "07": "Chapter-7-Analyzing-Behavior-Change-Basic-Assumptions-and-Strategies-6.pdf",
  "08": "Chapter-8-Reversal-and-Multielement-Designs-7.pdf",
  "09": "Chapter-9-Multiple-Baseline-and-Changing-Criterion-Designs-8.pdf",
  "10": "Chapter-10-Planning-and-Evaluating-Applied-Behavior-Analysis-Research-9.pdf",
  "11": "Chapter-11-Positive-Reinforcement-10.pdf",
  "12": "Chapter-12-Negative-Reinforcement.pdf",
  "13": "Chapter-13-Schedules-of-Reinforcement-2.pdf",
  "14": "Chapter-14-Positive-Punishment-3.pdf",
  "15": "Chapter-15-Negative-Punishment-4.pdf",
  "16": "Chapter-16-Motivating-Operations-5.pdf",
  "17": "Chapter-17-Stimulus-Control-6.pdf",
  "18": "Chapter-18-Verbal-Behavior-7.pdf",
  "19": "Chapter-19-Equivalence-based-Instruction-8.pdf",
  "20": "Chapter-20-Engineering-Emergent-Learning-with-Nonequivalence-Relations-9.pdf",
  "21": "Chapter-21-Imitation-Modeling-and-Observational-Learning-10.pdf",
  "22": "Chapter-22-Shaping-11.pdf",
  "23": "Chapter-23-Chaining.pdf",
  "24": "Chapter-24-Extinction-2.pdf",
  "25": "Chapter-25-Differential-Reinforcement-3.pdf",
  "26": "Chapter-26-Antecedent-Interventions-4.pdf",
  "27": "Chapter-27-Functional-Behavior-Assessment-5.pdf",
  "28": "Chapter-28-Token-Economy-Group-Contingencies-and-Contingency-Contracting-6.pdf",
  "29": "Chapter-29-Self-Management-7.pdf",
  "30": "Chapter-30-Generalization-and-Maintenance-of-Behavior-Change-8.pdf",
  "31": "Chapter-31-Ethical-and-Professional-Responsibilities-of-Applied-Behavior-Analysts-9.pdf",
};

/** Hand-curated modules override generated ones. Add additional curated chapters here as they're authored. */
const CURATED: Record<string, ChapterModule> = {
  "01": chapter01Module,
  "02": chapter02Module,
  "03": chapter03Module,
  "04": chapter04Module,
  "05": chapter05Module,
  "06": chapter06Module,
  "07": chapter07Module,
  "08": chapter08Module,
  "09": chapter09Module,
  "10": chapter10Module,
  "11": chapter11Module,
  "12": chapter12Module,
  "13": chapter13Module,
  "14": chapter14Module,
  "15": chapter15Module,
  "16": chapter16Module,
  "17": chapter17Module,
  "18": chapter18Module,
  "19": chapter19Module,
  "20": chapter20Module,
  "21": chapter21Module,
  "22": chapter22Module,
  "23": chapter23Module,
  "24": chapter24Module,
  "25": chapter25Module,
  "26": chapter26Module,
  "27": chapter27Module,
  "28": chapter28Module,
  "29": chapter29Module,
  "30": chapter30Module,
  "31": chapter31Module,
};

/** Final module registry - every id 01..31 maps to a ChapterModule. */
export const MODULES: Record<string, ChapterModule> = {};
for (const c of CATALOG) {
  const id = pad(c.number);
  const curated = CURATED[id];
  const gen = GENERATED_MODULES[id];
  // Always start from a base shape derived from the catalog so meta is canonical.
  const base = curated ?? gen;
  if (base) {
    const statusEntry = CURATION_STATUS[id];
    const isCurated = statusEntry?.status === "curated" || base.curated === true;
    const pdfFile = CHAPTER_PDFS[id];
    MODULES[id] = {
      ...base,
      meta: {
        ...base.meta,
        id,
        number: c.number,
        title: c.title,
        subtitle: c.subtitle,
        part: c.part,
        pdfHref: pdfFile ? `/api/pdfs/${pdfFile}` : base.meta.pdfHref,
      },
      curated: isCurated,
      // Approving via the curation workflow clears the chapter-level needsReview pill.
      needsReview: isCurated ? false : base.needsReview,
    };
  } else {
    // Safety fallback (should never trigger because the generator covers all 31).
    const pdfFile = CHAPTER_PDFS[id];
    MODULES[id] = {
      meta: {
        id, number: c.number, title: c.title, subtitle: c.subtitle, part: c.part,
        pdfHref: pdfFile ? `/api/pdfs/${pdfFile}` : undefined,
      },
      overview: ["Content not yet available."],
      objectives: [], coreConcepts: [], glossary: [], procedures: [],
      examples: [], comparisons: [], misconceptions: [], examChecklist: [],
      quiz: [], reviewPrompts: [], recap: [],
    };
  }
}

/** Flat list of all 31 chapters (in catalog order) with their full module. */
export const CHAPTERS: ChapterModule[] = CATALOG.map(c => MODULES[pad(c.number)]);

/** Group chapters by Part for the library layout. */
export const CHAPTER_GROUPS: { part: string; items: ChapterModule[] }[] = (() => {
  const map = new Map<string, ChapterModule[]>();
  for (const c of CHAPTERS) {
    if (!map.has(c.meta.part)) map.set(c.meta.part, []);
    map.get(c.meta.part)!.push(c);
  }
  return Array.from(map.entries()).map(([part, items]) => ({ part, items }));
})();

export function getModule(id: string): ChapterModule | undefined {
  return MODULES[id];
}

export const TOTAL_COUNT = CHAPTERS.length;
export const CURATED_COUNT = CHAPTERS.filter(c => c.curated).length;
